import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Lock, Link2, FileCheck2, ArrowRight, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { getDraws } from "@/lib/api";
import { TIPO_LABEL, VISIBLE_TIPOS, formatResultado, formatDate } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";

const GAMES = [
  { tipo: "triple", desc: "Triple A y Triple B (000–999) con Estado, sorteo digital verificable." },
  { tipo: "animalitos", desc: "Una figura ganadora entre los animalitos, con figura comodín." },
];

const PILLARS = [
  { icon: Lock, t: "Compromiso previo", d: "Publicamos el hash SHA-256 de la semilla antes de cada sorteo." },
  { icon: ShieldCheck, t: "Provably-Fair", d: "Resultados reproducibles con HMAC-SHA256 y muestreo sin sesgo." },
  { icon: Link2, t: "Bitácora inmutable", d: "Cada sorteo se encadena por hash; cualquier cambio se detecta." },
  { icon: FileCheck2, t: "Acta certificada", d: "Descarga el acta PDF con toda la evidencia de auditoría." },
];

export default function Home() {
  const [draws, setDraws] = useState([]);
  useEffect(() => {
    getDraws({ estado: "revelado", limit: 20 })
      .then((d) => setDraws(d.filter((x) => VISIBLE_TIPOS.includes(x.tipo)).slice(0, 6)))
      .catch(() => {});
  }, []);

  return (
    <div data-theme="institutional">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border bg-card">
        <div className="noise-overlay" />
        <div className="relative mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-10 lg:py-24">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1 text-xs font-medium text-muted-foreground">
              <ShieldCheck className="h-3.5 w-3.5 text-primary" /> Aleatoriedad verificable (Provably-Fair)
            </span>
            <h1 className="mt-5 font-display text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl">
              Sorteos digitales <span className="text-primary">verificables</span> por cualquiera
            </h1>
            <p className="mt-5 max-w-2xl text-base text-muted-foreground sm:text-lg">
              Plataforma de aleatoriedad provably-fair con bitácora inmutable. Publicamos el compromiso antes del sorteo y revelamos la semilla después, para que el resultado sea reproducible e imposible de manipular.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/verificar"><Button size="lg" className="gap-2" data-testid="hero-verify-cta"><Search className="h-4 w-4" /> Verificar un sorteo</Button></Link>
              <Link to="/juego/triple"><Button size="lg" variant="outline" className="gap-2">Ver resultados <ArrowRight className="h-4 w-4" /></Button></Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pilares */}
      <section className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-10">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {PILLARS.map((p) => (
            <Card key={p.t} className="border-border">
              <CardContent className="p-5">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary text-primary"><p.icon className="h-5 w-5" /></span>
                <p className="mt-3 font-display font-semibold">{p.t}</p>
                <p className="mt-1 text-sm text-muted-foreground">{p.d}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Juegos */}
      <section className="mx-auto max-w-6xl px-4 pb-4 sm:px-6 lg:px-10">
        <h2 className="font-display text-xl font-bold">Juegos disponibles</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {GAMES.map((g) => (
            <Link key={g.tipo} to={`/juego/${g.tipo}`} data-testid={`home-game-${g.tipo}`} data-theme={g.tipo}>
              <Card className="group h-full overflow-hidden border-border transition-shadow hover:shadow-md">
                <div className="h-2" style={{ background: "hsl(var(--game-accent))" }} />
                <CardContent className="p-5">
                  <p className="font-display text-lg font-bold" style={{ color: "hsl(var(--game-accent))" }}>{TIPO_LABEL[g.tipo]}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{g.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-foreground">Ver resultados <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </section>

      {/* Últimos sorteos */}
      <section className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-10">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-bold">Últimos sorteos revelados</h2>
          <Link to="/bitacora" className="text-sm font-medium text-primary hover:underline">Ver bitácora</Link>
        </div>
        {draws.length === 0 ? (
          <Card className="mt-4 border-dashed"><CardContent className="p-8 text-center text-sm text-muted-foreground">Aún no hay sorteos revelados. Crea uno desde la <Link to="/consola/sorteos" className="text-primary underline">consola</Link>.</CardContent></Card>
        ) : (
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {draws.map((d) => (
              <Link key={d.id} to={`/sorteo/${d.id}`} data-theme={d.tipo} data-testid={`home-draw-${d.id}`}>
                <Card className="h-full border-border transition-shadow hover:shadow-md">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: "hsl(var(--game-accent))" }}>{TIPO_LABEL[d.tipo]}</span>
                      <StatusBadge estado={d.estado} />
                    </div>
                    <p className="mt-3 font-display text-lg font-bold">{formatResultado(d.tipo, d.resultado)}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{formatDate(d.executed_at)}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
